import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, MapPinIcon, PhoneIcon, MailIcon, CheckCircleIcon, BookOpenIcon, BriefcaseIcon, ChevronDownIcon, ChevronLeftIcon, AwardIcon, BuildingIcon, LoaderIcon } from 'lucide-react';
import { getUniversityBySlug, getProgramBySlug, University, UniversityProgram } from '../data/universities';
import { useFormState } from '../hooks/useFormState';
import { educationLevels } from '../data/degrees';
import { TCPAConsent } from '../components/TCPAConsent';
import { TrustSignals } from '../components/TrustSignals';
const startDateOptions = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 30; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
export function UniversityPage() {
  const {
    universitySlug,
    programSlug
  } = useParams<{
    universitySlug: string;
    programSlug?: string;
  }>();
  const navigate = useNavigate();
  const [university, setUniversity] = useState<University | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<UniversityProgram | null>(null);
  const [formStep, setFormStep] = useState(0); // 0=program select, 1=education, 2=zip/address, 3=contact
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  useEffect(() => {
    if (universitySlug) {
      const foundUni = getUniversityBySlug(universitySlug);
      if (foundUni) {
        setUniversity(foundUni);
        if (programSlug) {
          const foundProg = getProgramBySlug(foundUni, programSlug);
          if (foundProg) {
            setSelectedProgram(foundProg);
            if (formStep === 0) setFormStep(1);
          } else {
            setSelectedProgram(null);
          }
        } else {
          setSelectedProgram(null);
          setFormStep(0);
        }
      } else {
        setUniversity(null);
      }
    }
    window.scrollTo(0, 0);
  }, [universitySlug, programSlug]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    if (newSlug) {
      navigate(`/colleges/${universitySlug}/${newSlug}`, {
        replace: true
      });
    } else {
      navigate(`/colleges/${universitySlug}`, {
        replace: true
      });
    }
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        // Fallback: let user enter manually
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (validateUniStep2()) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = () => {
    if (validateUniStep3()) {
      console.log('Lead submitted:', formData);
      navigate('/thank-you', {
        state: {
          formData
        }
      });
    }
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  if (!university) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-612">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-613">
          University Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-614">
          We couldn't find the university you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-615">
          Return Home
        </Link>
      </div>;
  }
  const renderFormContent = () => {
    // Step 0: Just program selector (shown when no program selected)
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-616">
          <div data-id="element-617">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-618">
              Select a Program
            </label>
            <div className="relative" data-id="element-619">
              <select id="hero-program-select" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-navy focus:border-navy font-medium text-gray-800" data-id="element-620">
                <option value="" data-id="element-621">-- Choose a Program --</option>
                {university.programs.map(p => <option key={p.slug} value={p.slug} data-id="element-622">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500" data-id="element-623">
                <ChevronDownIcon className="h-4 w-4" data-id="element-624" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-625">
            Select a program above to get started
          </p>
        </div>;
    }
    // Step 1: Education level, start date, grad year
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-626">
          {/* Selected program (locked in) */}
          <div data-id="element-627">
            <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-628">
              Program
            </label>
            <div className="px-3 py-2.5 text-sm border border-gray-200 rounded-md bg-gray-50 font-medium text-gray-700" data-id="element-629">
              {selectedProgram?.title}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-630" />

          {/* Education Level */}
          <div data-id="element-631">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-632">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-633">
              <option value="" data-id="element-634">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-635">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-636">
                {errors.currentEducation}
              </p>}
          </div>

          {/* Start Date */}
          <div data-id="element-637">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-638">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-639">
              <option value="" data-id="element-640">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-641">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-642">{errors.startDate}</p>}
          </div>

          {/* Grad Year */}
          <div data-id="element-643">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-644">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-645">
              <option value="" data-id="element-646">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-647">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-648">{errors.gradYear}</p>}
          </div>

          <button type="button" onClick={handleStep1Next} className="w-full bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-649">
            Next
          </button>
        </div>;
    }
    // Step 2: Zip code → city/state/address
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-650">
          <div data-id="element-651">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-652">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) {
              lookupZip(val);
            }
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-653" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-654">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-655">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-656" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-657">
                <div data-id="element-658">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-659">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-660" />
                </div>
                <div data-id="element-661">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-662">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-663" />
                </div>
              </div>

              <div data-id="element-664">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-665">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-666">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-667" />
              </div>
            </>}

          <div className="flex gap-3" data-id="element-668">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-669">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-670" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-671">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 3: Contact info + TCPA
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-672">
          <div className="grid grid-cols-2 gap-3" data-id="element-673">
            <div data-id="element-674">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-675">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-676" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-677">{errors.firstName}</p>}
            </div>

            <div data-id="element-678">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-679">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-680" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-681">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-682">
            <div data-id="element-683">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-684">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-685" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-686">{errors.email}</p>}
            </div>

            <div data-id="element-687">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-688">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', e.target.value)} className={inputClasses(!!errors.phone)} placeholder="(555) 123-4567" data-id="element-689" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-690">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2" data-id="element-691">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-692">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy cursor-pointer flex-shrink-0" data-id="element-693" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-694">
                By submitting this form, you are giving your express written
                consent to be contacted regarding our services using email,
                telephone or text, including our use of automated technology for
                calls or texts to the number provided above. This consent is not
                required to purchase goods or services. and you may always call
                us directly.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-695">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3" data-id="element-696">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-697">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-698" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} className="flex-1 bg-crimson hover:bg-crimson-dark text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-699">
              Submit
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <main className="flex-grow bg-light pb-16" data-id="element-700">
      {/* College Header */}
      <header className="bg-white border-b border-gray-200" data-id="element-701">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between" data-id="element-702">
          <Link to={`/colleges/${universitySlug}`} className="flex items-center" data-id="element-703">
            <img src="https://cdn.magicpatterns.com/uploads/bdkvCHGoAUmVRHbhhPnoYA/pasted-image.webp" alt={`${university.name} logo`} className="h-12 md:h-14 w-auto object-contain" data-id="element-704" />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-705">
            <span className="hidden sm:inline-flex items-center" data-id="element-706">
              <PhoneIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-707" />
              {university.phone}
            </span>
            <a href={`mailto:${university.email}`} className="hidden md:inline-flex items-center hover:text-navy transition-colors" data-id="element-708">
              <MailIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-709" />
              {university.email}
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#1B365D] via-[#102442] to-[#8B1A1A]" data-id="element-710">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10 md:py-16" data-id="element-711">
          <div className="inline-flex items-center text-gold font-semibold tracking-wider text-sm uppercase mb-6" data-id="element-712">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-713" />
            {university.name} &middot; {university.location}
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start" data-id="element-714">
            {/* Left: Program Info */}
            <div className="lg:w-1/2 flex flex-col justify-center" data-id="element-715">
              <AnimatePresence mode="wait" data-id="element-716">
                <motion.div key={selectedProgram?.slug || 'overview'} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.25
              }} data-id="element-717">
                  <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold text-white mb-5 leading-tight" data-id="element-718">
                    {selectedProgram ? selectedProgram.title : `Explore Programs at ${university.name}`}
                  </h1>
                  <p className="text-base md:text-lg text-gray-200 leading-relaxed mb-6" data-id="element-719">
                    {selectedProgram ? selectedProgram.description : university.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {!selectedProgram && <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium self-start" data-id="element-720">
                  <ChevronDownIcon className="w-4 h-4 mr-2 animate-bounce" data-id="element-721" />
                  Select a program to get started
                </div>}

              {selectedProgram && <div className="flex flex-wrap gap-3 text-sm text-gray-300" data-id="element-722">
                  {selectedProgram.keyFacts.slice(0, 2).map((fact, idx) => <span key={idx} className="inline-flex items-center bg-white/10 border border-white/15 rounded-full px-3 py-1" data-id="element-723">
                      <CheckCircleIcon className="w-3.5 h-3.5 text-green-400 mr-1.5" data-id="element-724" />
                      {fact}
                    </span>)}
                </div>}
            </div>

            {/* Right: Form Card */}
            <div className="lg:w-1/2 w-full flex justify-center lg:justify-end" data-id="element-725">
              <div className="w-full max-w-md" data-id="element-726">
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-727">
                  <div className="bg-crimson px-6 py-3.5" data-id="element-728">
                    <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-729">
                      Request Information
                    </h2>
                  </div>

                  {/* Progress Bar */}
                  {formStep > 0 && <div className="px-6 pt-4" data-id="element-730">
                      <div className="flex justify-between items-center mb-1.5" data-id="element-731">
                        <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-732">
                          Step {formStep} of {totalSteps}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-733">
                          {progressPercent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-734">
                        <motion.div className="h-full bg-gold rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${progressPercent}%`
                    }} transition={{
                      duration: 0.4,
                      ease: 'easeOut'
                    }} data-id="element-735" />
                      </div>
                    </div>}

                  <div className="p-6" data-id="element-736">
                    <AnimatePresence mode="wait" data-id="element-737">
                      <motion.div key={formStep} initial={{
                      opacity: 0,
                      x: 30
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} exit={{
                      opacity: 0,
                      x: -30
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-738">
                        {renderFormContent()}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <p className="text-center text-gray-300 text-sm mt-3 italic" data-id="element-739">
                  Used by{' '}
                  <strong className="text-white font-semibold" data-id="element-740">
                    2M+ students
                  </strong>{' '}
                  to choose the right school.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TrustSignals data-id="element-741" />

      {/* Program Content — Full Width */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-742">
        <AnimatePresence mode="wait" data-id="element-743">
          {selectedProgram ? <motion.div key={selectedProgram.slug} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="space-y-8" data-id="element-744">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-745">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-746">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-747">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-748">
                      <BriefcaseIcon className="w-6 h-6 text-navy" data-id="element-749" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-750">
                      Potential Job Opportunities
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 font-medium" data-id="element-751">
                    {selectedProgram.jobOpportunitiesIntro}:
                  </p>
                  <ul className="space-y-3" data-id="element-752">
                    {selectedProgram.jobOpportunities.map((job, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-753">
                        <span className="w-1.5 h-1.5 rounded-full bg-navy mt-1.5 mr-3 flex-shrink-0" data-id="element-754" />
                        <span data-id="element-755">{job}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-756">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-757">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-758">
                      <AwardIcon className="w-6 h-6 text-navy" data-id="element-759" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-760">Key Facts</h2>
                  </div>
                  <ul className="space-y-4" data-id="element-761">
                    {selectedProgram.keyFacts.map((fact, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-762">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" data-id="element-763" />
                        <span className="leading-tight pt-0.5" data-id="element-764">{fact}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-765">
                <div className="bg-navy px-6 py-4 flex items-center" data-id="element-766">
                  <BookOpenIcon className="w-5 h-5 text-gold mr-3" data-id="element-767" />
                  <h2 className="text-lg font-bold text-white" data-id="element-768">
                    Program Curriculum
                  </h2>
                </div>
                <table className="min-w-full divide-y divide-gray-200" data-id="element-769">
                  <thead className="bg-gray-50" data-id="element-770">
                    <tr data-id="element-771">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-772">
                        Course Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-773">
                        Credits
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100" data-id="element-774">
                    {selectedProgram.curriculum.map((item, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-775">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-776">
                          {item.course}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-777">
                          {item.credits}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </motion.div> : <motion.div key="default-overview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="space-y-8" data-id="element-778">
              {/* About the College */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-779">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-780">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-781">
                    <BuildingIcon className="w-6 h-6 text-navy" data-id="element-782" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-783">
                    About {university.name}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-784">
                  {university.description}
                </p>
                <p className="text-gray-600 leading-relaxed" data-id="element-785">
                  Global Tech College is committed to providing career-focused
                  education that prepares students for in-demand careers. With
                  flexible online and on-campus options, students can earn their
                  degree on their schedule. Our programs are designed with input
                  from industry professionals to ensure graduates have the
                  skills employers are looking for.
                </p>
              </div>

              {/* Highlights Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-786">
                {[{
              icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-787" />,
              title: 'Title IV Approved',
              desc: 'Federal financial aid available for eligible students'
            }, {
              icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-788" />,
              title: 'VA Benefits',
              desc: 'Approved for veterans and dependents education benefits'
            }, {
              icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-789" />,
              title: 'TechCred Eligible',
              desc: 'Ohio TechCred funding available for qualifying programs'
            }, {
              icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-790" />,
              title: 'WIOA Approved',
              desc: 'Workforce Innovation and Opportunity Act approved training'
            }].map((item, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-center" data-id="element-791">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-crimson/5 mb-3" data-id="element-792">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-navy text-sm mb-1" data-id="element-793">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-794">{item.desc}</p>
                  </div>)}
              </div>

              {/* Available Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-795">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-796">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-797">
                    <BookOpenIcon className="w-6 h-6 text-navy" data-id="element-798" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-799">
                    Available Programs
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-800">
                  {university.programs.map(prog => <button key={prog.slug} onClick={() => navigate(`/colleges/${universitySlug}/${prog.slug}`, {
                replace: true
              })} className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-navy/30 hover:bg-navy/[0.02] transition-all text-left group" data-id="element-801">
                      <div className="p-1.5 bg-navy/5 rounded-md group-hover:bg-navy/10 transition-colors mt-0.5" data-id="element-802">
                        <GraduationCapIcon className="w-4 h-4 text-navy" data-id="element-803" />
                      </div>
                      <div data-id="element-804">
                        <h3 className="font-semibold text-navy text-sm group-hover:text-navy-light transition-colors" data-id="element-805">
                          {prog.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-806">
                          {prog.degreeLevel === '1' ? 'Certificate' : 'Associate Degree'}{' '}
                          · {prog.keyFacts[0]}
                        </p>
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Accreditation */}
              <div className="bg-navy/5 rounded-xl border border-navy/10 p-6 flex items-start gap-4" data-id="element-807">
                <div className="p-2 bg-white rounded-lg shadow-sm" data-id="element-808">
                  <AwardIcon className="w-6 h-6 text-navy" data-id="element-809" />
                </div>
                <div data-id="element-810">
                  <h3 className="font-bold text-navy mb-1" data-id="element-811">Accreditation</h3>
                  <p className="text-sm text-gray-600" data-id="element-812">
                    {university.accreditation}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-813">
                    {university.location}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Contact Info Bar */}
      <div className="bg-navy-dark" data-id="element-814">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-815">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-816">
            <div className="flex items-center gap-3" data-id="element-817">
              <div className="p-2 bg-white/10 rounded-lg" data-id="element-818">
                <GraduationCapIcon className="w-8 h-8 text-gold" data-id="element-819" />
              </div>
              <div data-id="element-820">
                <h3 className="text-white font-bold text-lg" data-id="element-821">
                  {university.name}
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-822">
                  {university.accreditation}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-300" data-id="element-823">
              <span className="inline-flex items-center" data-id="element-824">
                <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" data-id="element-825" />
                {university.location}
              </span>
              <span className="inline-flex items-center" data-id="element-826">
                <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" data-id="element-827" />
                {university.phone}
              </span>
              <span className="inline-flex items-center" data-id="element-828">
                <MailIcon className="w-4 h-4 mr-2 text-gray-400" data-id="element-829" />
                {university.email}
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>;
}